import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Inbox, TrendingUp, FileText, AlertCircle } from "lucide-react";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div>Loading…</div>;

  const stats = [
    { label: "Total enquiries", v: d.totals.enquiries, icon: Inbox },
    { label: "New this week", v: d.totals.recent_7d, icon: TrendingUp },
    { label: "Published projects", v: d.totals.published_projects, icon: FileText },
    { label: "Drafts", v: d.totals.draft_projects, icon: AlertCircle },
  ];

  return (
    <div data-testid="page-admin-dashboard" className="max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-2">Overview</div>
          <h1 className="font-serif text-4xl tracking-tight">Studio dashboard</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} data-testid={`stat-${i}`} className="bg-white border border-[#D5D0C8] p-5 rounded-sm">
              <div className="flex items-center justify-between text-[#7A736C]"><span className="text-[10px] uppercase tracking-[0.28em]">{s.label}</span><Icon size={14} /></div>
              <div className="font-serif text-4xl tracking-tight mt-3">{s.v}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[#D5D0C8] p-6 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-4">Enquiries by status</div>
          <ul className="space-y-2 text-sm">
            {Object.entries(d.enquiries_by_status).map(([k, v]) => (
              <li key={k} className="flex justify-between py-2 border-b border-[#EAE6DF]"><span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium">{v}</span></li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-[#D5D0C8] p-6 rounded-sm lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C]">Recent enquiries</div>
            <Link to="/admin/enquiries" className="text-xs underline underline-offset-4 hover:text-[#A85C42]">View all →</Link>
          </div>
          <ul className="divide-y divide-[#EAE6DF]">
            {d.recent_enquiries.map((e) => (
              <li key={e.id} className="py-3">
                <Link to={`/admin/enquiries/${e.id}`} className="flex justify-between items-center hover:bg-[#F9F8F6] px-2 -mx-2 py-1 rounded">
                  <div>
                    <div className="text-sm font-medium">{e.name} <span className="text-[#7A736C]">· {e.email}</span></div>
                    <div className="text-xs text-[#7A736C]">{e.project_location || "—"} · {e.project_type || "—"} · {(e.services || []).slice(0, 2).join(", ")}</div>
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#A85C42]">{e.status}</div>
                </Link>
              </li>
            ))}
            {d.recent_enquiries.length === 0 && <li className="text-sm text-[#7A736C] py-4">No enquiries yet.</li>}
          </ul>
        </div>
        <div className="bg-white border border-[#D5D0C8] p-6 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-4">Top viewed projects</div>
          <ul className="space-y-3">
            {d.top_projects.map((p) => (
              <li key={p.slug} className="flex items-center gap-3">
                {p.cover_image && <img src={p.cover_image} alt="" className="w-12 h-12 object-cover rounded-sm" />}
                <div className="flex-1"><div className="text-sm">{p.title}</div><div className="text-xs text-[#7A736C]">{p.views} views</div></div>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-[#D5D0C8] p-6 rounded-sm lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-4">Content health</div>
          <ul className="text-sm space-y-2">
            <li className="flex justify-between py-2 border-b border-[#EAE6DF]"><span>Projects missing SEO description</span><span className="text-[#A85C42]">{d.content_health.missing_seo_descriptions}</span></li>
            <li className="flex justify-between py-2 border-b border-[#EAE6DF]"><span>Gallery images missing alt text</span><span className="text-[#A85C42]">{d.content_health.images_missing_alt}</span></li>
            <li className="flex justify-between py-2"><span>Published blog posts</span><span>{d.totals.published_blog}</span></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
